<template>
  <v-dialog
    v-if="featureEnabled"
    :value="open"
    width="480"
    @click:outside="handleClose"
    @keydown.esc="handleClose"
  >
    <v-card class="sid-card pa-6 background-pattern-icon" data-testid="scribe-import-dialog">
      <div class="d-flex justify-end mb-2">
        <v-btn icon color="grey" aria-label="Close" data-testid="sid-close" @click="handleClose">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>

      <h2 id="sid-title" class="sid-title" data-testid="sid-title">{{ stateTitle }}</h2>
      <p class="sid-description" data-testid="sid-description">
        {{ stateDescription }}
      </p>

      <!-- ── ENTRY: link field + Import ── -->
      <template v-if="phase === 'entry'">
        <div class="sid-field">
          <label class="sid-label" for="sid-link-input" data-testid="sid-link-label">
            Scribe link
            <span class="sid-required" aria-hidden="true">*</span>
          </label>
          <input
            id="sid-link-input"
            v-model="urlModel"
            class="sid-input"
            :class="{ 'sid-input--filled': hasUrl, 'sid-input--invalid': visibleError }"
            type="text"
            placeholder="https://scribehow.com/shared/…"
            autocomplete="off"
            required
            :aria-required="true"
            :aria-invalid="Boolean(visibleError)"
            :aria-describedby="visibleError ? 'sid-error' : undefined"
            data-testid="sid-link-input"
            @keydown.enter.prevent="handleSubmit"
          />
          <p
            v-if="visibleError"
            id="sid-error"
            class="sid-error"
            role="alert"
            data-testid="sid-error"
          >
            {{ visibleError }}
          </p>
        </div>

        <div class="sid-footer">
          <button
            type="button"
            class="sid-btn sid-btn--primary"
            :class="{ 'sid-btn--disabled': !canSubmit }"
            :disabled="!canSubmit"
            :aria-busy="isStarting"
            data-testid="sid-submit"
            @click="handleSubmit"
          >
            {{ isStarting ? "Importing…" : "Import" }}
          </button>
        </div>
      </template>

      <!-- ── STARTING / IMPORTING: progress ── -->
      <template v-else-if="phase === 'starting' || phase === 'importing'">
        <div class="sid-progress-area">
          <div
            class="sid-progress"
            role="progressbar"
            aria-valuemin="0"
            :aria-valuemax="progressTotal || 0"
            :aria-valuenow="progressDone"
            :aria-valuetext="progressCount"
            data-testid="sid-progress"
          >
            <div
              class="sid-progress__fill"
              :style="{ width: `${progressPercent}%` }"
              data-testid="sid-progress-fill"
            />
          </div>
          <div class="sid-progress-meta">
            <span class="sid-progress-status" data-testid="sid-status">
              {{ progressStatus }}
            </span>
            <span v-if="phase === 'importing'" class="sid-progress-count" data-testid="sid-count">
              {{ progressCount }}
            </span>
          </div>
        </div>

        <div class="sid-footer">
          <button
            type="button"
            class="sid-btn sid-btn--secondary"
            data-testid="sid-cancel"
            @click="handleClose"
          >
            Cancel
          </button>
        </div>
      </template>

      <!-- ── FAILED ── -->
      <template v-else-if="phase === 'failed'">
        <p class="sid-error" role="alert" data-testid="sid-error">{{ failedErrorMessage }}</p>

        <div class="sid-footer">
          <button
            type="button"
            class="sid-btn sid-btn--secondary"
            data-testid="sid-cancel"
            @click="handleClose"
          >
            Cancel
          </button>
          <button
            type="button"
            class="sid-btn sid-btn--primary"
            data-testid="sid-retry"
            @click="retry"
          >
            Try again
          </button>
        </div>
      </template>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { computed, getCurrentInstance, onMounted, ref, watch } from "vue";

import { LD_FLAG_IDEA_MDP_604 } from "@components/constants/launchDarklyFlagKeys";
import {
  isValidScribeLink,
  SCRIBE_LINK_INVALID_INPUT_MESSAGE,
} from "@components/utils/scribeImport";
import { useScribeImportJob } from "@components/utils/useScribeImportJob";

/**
 * ER-11752 + ER-12335 — ScribeImportDialog (Article Checkpoint import from Scribe).
 *
 * Untitled-UI single-field modal that collects a public Scribe share link, then starts
 * the background job and shows its progress. ER-12323 moved the import to a 202 job id
 * plus a status endpoint, so the dialog now polls and renders the Starting / Importing /
 * Failed states from the Figma component set, keeping the original link-entry state
 * unchanged. Success emits `success` with the result so the parent populates the editor.
 *
 * The parent injects the two API calls (`startImport`, `pollStatus`) as props — the dialog
 * stays free of `@/api`/store imports so it remains storybook-safe and unit-testable.
 * Shipped behind the linked idea flag (idea-MDP-604).
 */
const props = defineProps({
  /** Boolean that controls dialog visibility. */
  open: {
    type: Boolean,
    default: false,
  },
  accountId: {
    type: [Number, String],
    default: null,
  },
  responsibilityId: {
    type: [Number, String],
    default: null,
  },
  checkpointId: {
    type: [Number, String],
    default: null,
  },
  /** POST the import; resolves 202 `{ data: { job_id } }`, rejects 422 with a SCRIBE_IMPORT_* code. */
  startImport: {
    type: Function,
    default: null,
  },
  /** GET the job status; resolves `{ data: { status, progress } }`. */
  pollStatus: {
    type: Function,
    default: null,
  },
});

const emit = defineEmits(["success", "close"]);

const featureEnabled = ref(false);
const urlModel = ref("");
const clientValidationError = ref("");

const proxy = getCurrentInstance()?.proxy;

const job = useScribeImportJob({
  startImport: (payload) =>
    props.startImport?.(props.accountId, props.responsibilityId, props.checkpointId, payload),
  pollStatus: (jobId) =>
    props.pollStatus?.(props.accountId, props.responsibilityId, props.checkpointId, jobId),
  onSucceeded: (result) => {
    emit("success", result);
    emit("close");
  },
});

const phase = job.phase;
const isStarting = job.isStarting;
const progressDone = computed(() => job.progress.value.done);
const progressTotal = computed(() => job.progress.value.total);
const progressPercent = computed(() => {
  const total = progressTotal.value;
  if (!total) {
    return 0;
  }
  return Math.round((progressDone.value / total) * 100);
});

const hasUrl = computed(() => urlModel.value.trim().length > 0);

const canSubmit = computed(() => hasUrl.value && !isStarting.value);

const visibleError = computed(() => clientValidationError.value || job.entryError.value || "");

const stateTitle = computed(() => {
  if (phase.value === "failed") {
    return "Import failed";
  }
  if (phase.value === "importing") {
    return "Importing from Scribe";
  }
  return "Import from Scribe";
});

const stateDescription = computed(() => {
  if (phase.value === "failed") {
    return "Nothing was added to your article.";
  }
  if (phase.value === "importing") {
    return "Long guides can take a minute. Keep this window open until it finishes.";
  }
  if (phase.value === "starting") {
    return "Reading the guide and preparing its steps.";
  }
  return "Paste a public Scribe share link to import its steps into the article.";
});

const progressStatus = computed(() =>
  phase.value === "importing" ? "Downloading and re-hosting screenshots" : "Fetching the guide…"
);

const progressCount = computed(() => `${progressDone.value} of ${progressTotal.value}`);

const failedErrorMessage =
  "We couldn't import this guide. Try again, or contact support if it keeps failing.";

watch(
  () => props.open,
  (isOpen) => {
    // Reset the field + inline errors + job phase on every open so a stale link,
    // error or half-finished job never leaks across opens.
    if (isOpen) {
      urlModel.value = "";
      clientValidationError.value = "";
      job.reset();
    } else {
      job.cancel();
    }
  }
);

function handleSubmit() {
  if (!hasUrl.value || isStarting.value) {
    return;
  }
  const url = urlModel.value.trim();
  if (!isValidScribeLink(url)) {
    clientValidationError.value = SCRIBE_LINK_INVALID_INPUT_MESSAGE;
    return;
  }
  clientValidationError.value = "";
  job.start(url);
}

function handleClose() {
  job.cancel();
  emit("close");
}

function retry() {
  job.retry();
}

onMounted(async () => {
  try {
    featureEnabled.value = (await proxy?.$getFlag?.(LD_FLAG_IDEA_MDP_604, true)) !== false;
  } catch {
    // Kill-switch lookup failures never lock the dialog shut.
    featureEnabled.value = true;
  }
});
</script>

<style scoped>
.sid-card {
  background-color: #ffffff;
  border: 1px solid #e4e7ec;
  border-radius: 12px !important;
}

.sid-title {
  margin: 0 0 8px;
  color: #101828;
  font-size: 18px;
  font-weight: 600;
  line-height: 28px;
}

.sid-description {
  margin: 0 0 20px;
  color: #667085;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
}

.sid-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.sid-label {
  display: block;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: #344054;
}

.sid-required {
  color: #f04438;
}

.sid-input {
  box-sizing: border-box;
  width: 100%;
  height: 44px;
  padding: 10px 14px;
  background: #ffffff;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  font-family: inherit;
  font-size: 16px;
  line-height: 24px;
  color: #101828;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
}

.sid-input::placeholder {
  color: #667085;
}

.sid-input--filled {
  border-color: #7f56d9;
}

.sid-input--invalid {
  border-color: #f04438;
}

.sid-error {
  margin: 0 0 16px;
  font-size: 14px;
  line-height: 20px;
  color: #d92d20;
}

.sid-progress-area {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 24px;
}

.sid-progress {
  width: 100%;
  height: 8px;
  background: #eaecf0;
  border-radius: 4px;
  overflow: hidden;
}

.sid-progress__fill {
  height: 100%;
  background: #7f56d9;
  border-radius: 4px;
  transition: width 0.2s ease;
}

.sid-progress-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.sid-progress-status {
  color: #475467;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
}

.sid-progress-count {
  color: #344054;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  white-space: nowrap;
}

.sid-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 24px;
}

.sid-btn {
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 44px;
  min-width: 88px;
  padding: 0 16px;
  border-radius: 8px;
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  cursor: pointer;
}

.sid-btn--primary {
  background: #7f56d9;
  border: 1px solid #7f56d9;
  color: #ffffff;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
}

.sid-btn--secondary {
  background: #ffffff;
  border: 1px solid #d0d5dd;
  color: #344054;
}

/* Import (disabled/empty) state: #F2F4F7 background, no border, #98A2B3 text. */
.sid-btn--disabled,
.sid-btn:disabled {
  background: #f2f4f7;
  border: none;
  color: #98a2b3;
  box-shadow: none;
  cursor: not-allowed;
}

.sid-input:focus-visible,
.sid-btn:focus-visible {
  outline: 2px solid #7f56d9;
  outline-offset: 1px;
}

.background-pattern-icon {
  background-image: url("@components/assets/backgrounds/background_popup_icon.svg");
  background-size: 216px 216px;
  background-position: 0px 0px;
}
</style>
